import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Phone, MapPin, Navigation, Crosshair, Monitor, Music, Gem, Wrench, Target, ChevronRight, Clock, Award, Shield, DollarSign } from 'lucide-react';
import CITIES, { getCityUrl } from '@/data/cities';

const LOGO_URL = "/images/cave-city-pawn-logo.png";
const CAVEMAN_COUPLE = "/images/cavemen-couple.png";
const CAVEMAN_LAPTOP = "/images/caveman-laptop.png";

const categories = [
  { name: 'Firearms & Guns', icon: Crosshair, text: 'Buy, sell, or pawn handguns, rifles, shotguns, and ammunition from licensed dealers with fair appraisals.' },
  { name: 'Electronics', icon: Monitor, text: 'Laptops, tablets, smartphones, gaming consoles, and TVs. Get top dollar or find incredible pre-owned deals.' },
  { name: 'Musical Instruments', icon: Music, text: 'Guitars, amps, drums, keyboards and more. Quality gear at pawn shop prices for beginners to pros.' },
  { name: 'Jewelry & Gold', icon: Gem, text: 'Gold, silver, diamonds, watches. We buy gold and offer honest appraisals. Cash for jewelry today.' },
  { name: 'Power Tools', icon: Wrench, text: 'DeWalt, Milwaukee, Makita — quality power tools at unbeatable prices. Sell or pawn your used tools.' },
  { name: 'Archery Equipment', icon: Target, text: 'One of the largest selections around. Compound bows, recurve, crossbows, arrows and accessories.' },
];

const trustSignals = [
  { icon: Award, title: '35+ Years Experience', text: 'Decades of expertise in appraisals, lending, and customer service across the Florida Panhandle.' },
  { icon: Shield, title: 'No Credit Checks', text: 'Pawn loans with zero credit checks. Bring your items, get cash, and pick them up when you are ready.' },
  { icon: DollarSign, title: 'Same Day Cash', text: 'Walk in with your items and walk out with cash. Sell outright or get a pawn loan — your choice.' },
  { icon: Clock, title: 'Open 6 Days a Week', text: 'Monday through Friday 10AM–5PM, Saturday 10AM–2PM. Walk-ins always welcome.' },
];

const topCities = CITIES.filter(c => ['panama-city', 'dothan', 'chipley', 'quincy', 'bonifay', 'blountstown', 'bainbridge', 'headland', 'lynn-haven', 'graceville', 'chattahoochee', 'sneads'].includes(c.slug));

export default function PawnShopsNearMePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Pawn Shops Near Me | Cave City Pawn Shop - Marianna, FL | FL, AL & GA';
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Looking for pawn shops near me? Cave City Pawn Shop in Marianna, FL serves Florida, Alabama & Georgia. Buy, sell, or pawn firearms, electronics, jewelry, instruments, tools & archery gear. No credit check loans. 35+ years experience. Call 850-615-6105.');
    setMeta('keywords', 'pawn shops near me, pawn shop near me, nearest pawn shop, pawn shop Marianna FL, pawn shop Florida, pawn shop Alabama, pawn shop Georgia, buy sell pawn near me, cash for gold near me, gun shop near me, pawn loans near me, no credit check pawn shop');
    setMeta('og:title', 'Pawn Shops Near Me | Cave City Pawn Shop & Trading Company', 'property');
    setMeta('og:description', 'The best pawn shop near you! Cave City Pawn in Marianna, FL serves 45+ cities across FL, AL & GA. Firearms, electronics, jewelry, instruments, tools & archery. Call 850-615-6105.', 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:image', LOGO_URL, 'property');

    let schema = document.getElementById('nearme-schema');
    if (!schema) { schema = document.createElement('script'); schema.id = 'nearme-schema'; schema.type = 'application/ld+json'; document.head.appendChild(schema); }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Cave City Pawn Shop & Trading Company",
      "image": LOGO_URL,
      "telephone": "850-615-6105",
      "address": { "@type": "PostalAddress", "streetAddress": "4417 Jackson Street", "addressLocality": "Marianna", "addressRegion": "FL", "postalCode": "32448", "addressCountry": "US" },
      "geo": { "@type": "GeoCoordinates", "latitude": 30.7729, "longitude": -85.2305 },
      "url": "https://www.cavecitypawn.com/pawn-shops-near-me.html",
      "description": "The best pawn shop near you serving Florida, Alabama, and Georgia. Buy, sell, or pawn firearms, electronics, jewelry, musical instruments, power tools, and archery equipment. No credit check pawn loans. Over 35 years of experience.",
      "priceRange": "$$",
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "10:00", "closes": "17:00" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "10:00", "closes": "14:00" }
      ],
      "areaServed": [
        { "@type": "State", "name": "Florida" },
        { "@type": "State", "name": "Alabama" },
        { "@type": "State", "name": "Georgia" }
      ]
    });

    return () => {
      document.title = 'Cave City Pawn Shop | Buy, Sell & Pawn Guns, Electronics, Jewelry & More';
      const s = document.getElementById('nearme-schema');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Nav */}
      <nav className="bg-dirt-brown/95 backdrop-blur-sm shadow-deep py-2 sticky top-0 z-50" data-testid="nearme-navbar">
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 via-amber-700 to-amber-900 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-10" width="auto" height={40} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
              className="cave-btn px-4 py-2 bg-magma-red text-white font-heading text-xs sm:text-sm uppercase tracking-wider rounded-rock shadow-deep flex items-center gap-1.5">
              <ShoppingCart size={14} /> Shop Online
            </a>
            <a href="tel:850-615-6105"
              className="cave-btn px-3 py-2 border-2 border-accent text-accent font-heading text-xs sm:text-sm uppercase rounded-rock flex items-center gap-1.5 hover:bg-accent hover:text-dirt-brown transition-colors">
              <Phone size={14} /> <span className="hidden sm:inline">850-615-6105</span><span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative py-16 md:py-24 cave-gradient noise-overlay overflow-hidden" data-testid="nearme-hero">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-sm font-body text-stone-light/70">
                <li><Link to="/" className="hover:text-accent transition-colors">Home</Link></li>
                <li><ChevronRight size={14} /></li>
                <li className="text-accent">Pawn Shops Near Me</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading text-bone-white uppercase tracking-wide mb-5 leading-tight" data-testid="nearme-h1">
                  Pawn Shops<br /><span className="text-accent">Near Me</span>
                </h1>
                <p className="text-base md:text-lg text-stone-light font-body leading-relaxed mb-6">
                  Looking for the best pawn shop near you? <strong className="text-accent">Cave City Pawn Shop &amp; Trading Company</strong> in Marianna, Florida serves over <strong className="text-accent">45 cities</strong> across the Florida Panhandle, Southeast Alabama, and Southwest Georgia. With over 35 years of experience, we offer the best deals on firearms, electronics, musical instruments, jewelry, power tools, and archery equipment — plus no credit check pawn loans with same-day cash.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <a href="tel:850-615-6105" data-testid="nearme-call-btn"
                    className="cave-btn inline-flex items-center justify-center gap-2 px-8 py-4 bg-magma-red text-white font-heading text-lg uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                    <Phone size={22} /> Call 850-615-6105
                  </a>
                  <a href="https://www.google.com/maps/dir/?api=1&destination=4417+Jackson+Street,+Marianna,+FL+32448" target="_blank" rel="noopener noreferrer" data-testid="nearme-directions-btn"
                    className="cave-btn inline-flex items-center justify-center gap-2 px-8 py-4 border-3 border-bone-white text-bone-white font-heading text-lg uppercase rounded-rock hover:bg-bone-white hover:text-dirt-brown transition-all">
                    <Navigation size={22} /> Get Directions
                  </a>
                </div>
                <div className="bg-bone-white/10 border-2 border-bone-white/15 rounded-rock-lg p-4 inline-flex items-center gap-3 backdrop-blur-sm">
                  <MapPin size={20} className="text-accent shrink-0" />
                  <p className="text-sm text-stone-light font-body"><strong className="text-bone-white">4417 Jackson Street, Marianna, FL 32448</strong><br/>Mon-Fri 10AM-5PM | Sat 10AM-2PM</p>
                </div>
              </div>
              <div className="lg:col-span-2 hidden lg:flex items-center justify-center">
                <img src={CAVEMAN_COUPLE} alt="Cave City Pawn mascots" className="w-64 caveman-float drop-shadow-xl" loading="lazy" />
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 80L60 68C120 56 240 32 360 26C480 20 600 32 720 38C840 44 960 44 1080 38C1200 32 1320 20 1380 14L1440 8V80H0Z" fill="#F4EBD9"/>
            </svg>
          </div>
        </section>

        {/* Why We Are the Best Pawn Shop Near You */}
        <section className="py-16 md:py-20 bg-background noise-overlay" data-testid="nearme-trust">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Why Choose Us</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-dirt-brown uppercase chisel-heading">
                The Best Pawn Shop <span className="text-magma-red">Near You</span>
              </h2>
              <p className="text-base text-muted-foreground font-body max-w-2xl mx-auto mt-6">
                Whether you are searching for "pawn shops near me" from Dothan, Panama City, Chipley, or anywhere in the tri-state area, Cave City Pawn is worth the trip.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {trustSignals.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.title} className="stone-card bg-card border-3 border-dirt-brown/15 rounded-rock-lg p-6 text-center group">
                    <div className="category-icon w-14 h-14 bg-magma-red/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-magma-red transition-colors duration-300">
                      <Icon size={24} className="text-magma-red group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-base text-dirt-brown uppercase mb-2">{t.title}</h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">{t.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* What We Buy, Sell & Pawn */}
        <section className="py-16 md:py-20 bg-dirt-brown noise-overlay torch-glow relative" data-testid="nearme-categories">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -top-1 left-0 w-full">
            <path d="M0 0L80 10C160 20 320 40 480 46C640 52 800 44 960 38C1120 32 1280 28 1360 26L1440 24V0H0Z" fill="#F4EBD9"/>
          </svg>
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-accent uppercase tracking-widest mb-3">Our Inventory</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-bone-white uppercase chisel-heading">
                What We Buy, Sell &amp; <span className="text-accent">Pawn</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <div key={cat.name} className="stone-card bg-bone-white/8 border-3 border-bone-white/15 rounded-rock-lg p-6 backdrop-blur-sm hover:border-accent/30 transition-all group">
                    <div className="category-icon w-12 h-12 bg-accent/15 rounded-full flex items-center justify-center mb-4 group-hover:bg-accent transition-colors duration-300">
                      <Icon size={22} className="text-accent group-hover:text-dirt-brown transition-colors duration-300" />
                    </div>
                    <h3 className="font-heading text-lg text-bone-white uppercase mb-2">{cat.name}</h3>
                    <p className="text-sm text-stone-light/80 font-body leading-relaxed">{cat.text}</p>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-10">
              <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
                className="cave-btn inline-flex items-center gap-2 px-8 py-4 bg-accent text-dirt-brown font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                <ShoppingCart size={20} /> Browse Full Inventory Online
              </a>
            </div>
          </div>
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute -bottom-1 left-0 w-full">
            <path d="M0 60L80 50C160 40 320 20 480 14C640 8 800 16 960 22C1120 28 1280 32 1360 34L1440 36V60H0Z" fill="#F4EBD9"/>
          </svg>
        </section>

        {/* How Pawning Works */}
        <section className="py-16 md:py-20 bg-background noise-overlay" data-testid="nearme-how-it-works">
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Simple Process</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-dirt-brown uppercase chisel-heading">
                How Pawning <span className="text-magma-red">Works</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { num: '1', title: 'Bring Your Items', text: 'Walk into Cave City Pawn with anything of value — firearms, electronics, jewelry, tools, instruments, or archery gear. No appointment needed.' },
                { num: '2', title: 'Get Appraised', text: 'Our expert team appraises your items using our extensive reference library. We give you a fair, honest value based on 35+ years of experience.' },
                { num: '3', title: 'Get Paid', text: 'Choose to sell outright for cash or take a pawn loan. No credit checks ever. When you repay, you get your items back. It is that simple.' },
              ].map((step, i) => (
                <div key={step.num} className={`stone-card bg-card border-3 border-dirt-brown/15 rounded-rock-lg p-6 text-center ${i < 2 ? 'step-connector' : ''}`}>
                  <div className="w-14 h-14 bg-magma-red rounded-full flex items-center justify-center mx-auto mb-4 shadow-deep">
                    <span className="text-bone-white font-heading text-2xl">{step.num}</span>
                  </div>
                  <h3 className="font-heading text-lg text-dirt-brown uppercase mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cities We Serve */}
        <section className="py-16 md:py-20 bg-stone-light noise-overlay" data-testid="nearme-cities">
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Serving 3 States</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading text-dirt-brown uppercase chisel-heading">
                Cities &amp; Towns <span className="text-magma-red">We Serve</span>
              </h2>
              <p className="text-base text-muted-foreground font-body max-w-2xl mx-auto mt-6">
                No matter where you are searching "pawn shops near me" from, Cave City Pawn is here for you. We serve over 45 communities across Florida, Alabama, and Georgia.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
              {topCities.map(c => (
                <Link key={c.slug} to={getCityUrl(c)} data-testid={`nearme-city-${c.slug}`}
                  className="stone-card bg-bone-white border-2 border-dirt-brown/10 rounded-rock-lg p-4 text-center hover:border-magma-red/30 transition-all group">
                  <p className="font-heading text-sm text-dirt-brown uppercase group-hover:text-magma-red transition-colors">{c.name}, {c.state}</p>
                  <p className="text-xs text-muted-foreground font-body">{c.distance === 0 ? 'Our Home!' : `${c.distance} miles`}</p>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <Link to="/locations" data-testid="nearme-all-locations-link"
                className="cave-btn inline-flex items-center gap-2 px-7 py-3.5 bg-dirt-brown text-bone-white font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                <MapPin size={18} /> View All 45+ Service Areas
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-14 bg-dirt-brown" data-testid="nearme-final-cta">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <img src={CAVEMAN_LAPTOP} alt="Caveman at computer" className="w-28 mx-auto mb-4 drop-shadow-lg" loading="lazy" />
            <h2 className="text-2xl sm:text-3xl font-heading text-bone-white uppercase mb-3">
              Stop Searching. <span className="text-accent">Start Saving.</span>
            </h2>
            <p className="text-base text-stone-light font-body max-w-xl mx-auto mb-6">
              Cave City Pawn Shop is the answer to "pawn shops near me." Visit us at 4417 Jackson Street in Marianna, FL or call today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="tel:850-615-6105" className="cave-btn inline-flex items-center gap-2 px-8 py-4 bg-accent text-dirt-brown font-heading text-lg uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                <Phone size={22} /> Call 850-615-6105
              </a>
              <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
                className="cave-btn inline-flex items-center gap-2 px-8 py-4 bg-magma-red text-white font-heading text-lg uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                <ShoppingCart size={22} /> Shop Online
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-foreground text-bone-white py-10">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-dirt-brown via-stone-dark to-dirt-brown" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-12" />
              <div>
                <p className="font-body font-bold text-bone-white">4417 Jackson Street, Marianna, FL 32448</p>
                <a href="tel:850-615-6105" className="text-accent font-body font-bold hover:text-white transition-colors">850-615-6105</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-sm text-stone-light font-body hover:text-accent transition-colors">Home</Link>
              <Link to="/locations" className="text-sm text-stone-light font-body hover:text-accent transition-colors">All Locations</Link>
              <Link to="/sitemap" className="text-sm text-stone-light font-body hover:text-accent transition-colors">Sitemap</Link>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-stone-dark/30 text-center">
            <p className="text-xs text-stone-dark font-body">&copy; {new Date().getFullYear()} Cave City Pawn Shop &amp; Trading Company. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
